import React, { useState } from 'react';

import {
  Box,
  Card,
  Chip,
  Table,
  Paper,
  Stack,
  Alert,
  TableRow,
  Collapse,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  CardContent,
  Typography,
  TableContainer,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

import { SmsCreditWidget } from './sms-credit-widget';

export function SmsCreditsInfo() {
  const { t } = useTranslate('translation');
  const [expanded, setExpanded] = useState(false);

  const tr = (key: string, fallback: string) => {
    const val = t(key);
    return !val || val === key ? fallback : val;
  };

  return (
    <Box>
      <SmsCreditWidget sx={{ mb: 2 }} />

      <Card
        className="sms-credits-info"
        variant="outlined"
      sx={{
        mt: 3,
        mb: 2,
        borderRadius: 2,
        borderColor: 'divider',
        boxShadow: (theme) => theme.customShadows?.z8 || '0 4px 20px 0 rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* Header Banner */}
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          px: 3,
          py: 2.5,
          cursor: 'pointer',
          userSelect: 'none',
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
              : 'linear-gradient(135deg, #f0fdf4 0%, #e0f2fe 100%)',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            opacity: 0.95,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
            }}
          >
            <Iconify icon="solar:chat-round-money-bold" width={24} height={24} />
          </Box>
          <Box>
            <Typography
              component="h2"
              variant="h6"
              sx={{ fontWeight: 700, color: 'text.primary', m: 0 }}
            >
              {tr('HOW_SMS_CREDITS_WORK', 'How SMS Credits Work')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tr(
                'SMS_CREDITS_EXPLANATION',
                'Everything you need to know about message parts, rates, and credit usage'
              )}
            </Typography>
          </Box>
        </Box>

        <IconButton
          size="small"
          sx={{
            bgcolor: 'action.hover',
            transition: 'transform 0.2s ease-in-out',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <Iconify icon="solar:alt-arrow-down-bold" width={20} />
        </IconButton>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Stack spacing={3.5}>
          {/* What is an SMS credit */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:info-square-bold" width={20} />
              {tr('WHAT_IS_AN_SMS_CREDIT', 'What is an SMS credit?')}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.7, pl: 3.5 }}>
              {tr(
                'WHAT_IS_AN_SMS_CREDIT_BODY',
                'Every SMS you send uses credits from your balance. 1 credit = 1 message part (segment). Short messages use 1 credit; longer messages are split into several parts and use more.'
              )}
            </Typography>
          </Box>

          {/* How many credits does a message use */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:calculator-minimalistic-bold" width={20} />
              {tr('HOW_MANY_CREDITS_DOES_MESSAGE_USE', 'How many credits does a message use?')}
            </Typography>
            <Typography variant="body2" color="text.primary" sx={{ mb: 2, pl: 3.5 }}>
              {tr('HOW_MANY_CREDITS_BODY', 'It depends on the length and the language of your message:')}
            </Typography>

            {/* Table */}
            <Box sx={{ pl: { xs: 0, sm: 3.5 }, mb: 2 }}>
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 1.5, overflow: 'hidden' }}
              >
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'background.neutral' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, py: 1.5 }}>
                        {tr('LANGUAGE', 'Language')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1.5 }}>
                        {tr('ONE_CREDIT_COVERS', '1 credit covers')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, py: 1.5 }}>
                        {tr('LONGER_MESSAGES', 'Longer messages')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow hover>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_LANG_ENGLISH', 'English (and Latin letters)')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_COVER_160', 'up to 160 characters')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_EXTRA_160', 'every extra 160 characters = +1 credit')}
                      </TableCell>
                    </TableRow>
                    <TableRow hover>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_LANG_ARABIC', 'Arabic')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_COVER_70', 'up to 70 characters')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_EXTRA_70', 'every extra 70 characters = +1 credit')}
                      </TableCell>
                    </TableRow>
                    <TableRow hover>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_LANG_EMOJI', 'Emoji / special characters')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_COVER_280', 'up to 280 characters')}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {tr('SMS_EXTRA_280', 'every extra 280 characters = +1 credit')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Examples */}
            <Box sx={{ pl: { xs: 0, sm: 3.5 } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                {tr('SMS_EXAMPLES_HEADER', 'Examples:')}
              </Typography>
              <Box
                component="ul"
                sx={{
                  pl: 2.5,
                  m: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                  '& li': {
                    fontSize: '0.875rem',
                    color: 'text.primary',
                  },
                }}
              >
                <li>
                  {tr('SMS_EXAMPLE_1', '“Your order is ready 🎉” — short English →')}{' '}
                  <Chip label={tr('SMS_CREDIT_1', '1 credit')} size="small" color="success" variant="soft" sx={{ fontWeight: 700, height: 22 }} />
                </li>
                <li>
                  {tr('SMS_EXAMPLE_2', 'A 200-character English message →')}{' '}
                  <Chip label={tr('SMS_CREDITS_2', '2 credits')} size="small" color="info" variant="soft" sx={{ fontWeight: 700, height: 22 }} />
                </li>
                <li>
                  {tr('SMS_EXAMPLE_3', 'A 90-character Arabic message →')}{' '}
                  <Chip label={tr('SMS_CREDITS_2', '2 credits')} size="small" color="info" variant="soft" sx={{ fontWeight: 700, height: 22 }} />
                </li>
              </Box>

              {/* Tip */}
              <Alert
                severity="info"
                icon={<Iconify icon="solar:lightbulb-bold" color="warning.main" />}
                sx={{
                  mt: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'rgba(255,193,7,0.1)' : '#fff8e1',
                  borderColor: 'warning.light',
                  color: 'text.primary',
                }}
              >
                <em>{tr('SMS_TIP_TEXT', '💡 Tip: Arabic and emoji messages use more credits, because fewer characters fit in one part.')}</em>
              </Alert>
            </Box>
          </Box>

          {/* Buying credits */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:bag-check-bold" width={20} />
              {tr('BUYING_CREDITS', 'Buying credits')}
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 6,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                '& li': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
              }}
            >
              <li>
                {tr('SMS_BUY_BUNDLE_LI', 'Buy an SMS bundle (50,000 / 100,000 / 500,000 / 1,000,000).')}
              </li>
              <li>
                {tr('SMS_CREDITS_STACK_LI', 'Credits stack — buying another bundle adds to your balance.')}
              </li>
              <li>
                {tr('SMS_NO_RESET_LI', 'No monthly reset — your credits stay until you use them.')}
              </li>
            </Box>
          </Box>

          {/* When you run low or run out */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:danger-triangle-bold" width={20} />
              {tr('WHEN_YOU_RUN_LOW_OR_RUN_OUT', 'When you run low or run out')}
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 6,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                '& li': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
              }}
            >
              <li>{tr('SMS_LOW_WARN_LI', 'We’ll warn you when your balance is running low.')}</li>
              <li>
                {tr('SMS_NO_CREDITS_SENT_LI', 'If you don’t have enough credits for a message, it won’t be sent — just buy a bundle to continue.')}
              </li>
            </Box>
          </Box>

          {/* When are you charged? */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:card-transfer-bold" width={20} />
              {tr('WHEN_ARE_YOU_CHARGED', 'When are you charged?')}
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 6,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                '& li': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
              }}
            >
              <li>
                {tr('SMS_CHARGED_ACCEPTED_LI', 'A credit is used when your message is accepted for sending.')}
              </li>
              <li>
                {tr('SMS_NOT_CHARGED_ERROR_LI', 'If a message can’t be sent at all (e.g. a setup or number error), you’re not charged.')}
              </li>
              <li>
                {tr('SMS_CARRIER_STATUS_LI', 'Whether the message reaches the phone depends on the mobile carrier — you can see each message’s status (Sent / Delivered / Failed).')}
              </li>
            </Box>
          </Box>

          {/* Good to know */}
          <Box>
            <Typography
              component="h3"
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Iconify icon="solar:bookmark-star-bold" width={20} />
              {tr('GOOD_TO_KNOW', 'Good to know')}
            </Typography>
            <Box
              component="ul"
              sx={{
                pl: 6,
                m: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                '& li': {
                  fontSize: '0.875rem',
                  color: 'text.primary',
                },
              }}
            >
              <li>
                {tr('SMS_SEND_ONLY_LI', 'SMS is send-only — customers can’t reply by SMS.')}
              </li>
              <li>
                {tr('SMS_SEPARATE_BALANCE_LI', 'Your SMS credit balance is separate from any provider account balance.')}
              </li>
              <li>
                {tr('SMS_CHAR_COUNTER_LI', 'The character counter on the message box shows how many credits your message will use before you send.')}
              </li>
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Collapse>
    </Card>
    </Box>
  );
}

export default SmsCreditsInfo;
